import type { Project } from "../../types";

type ProjectAvailability = Project["availability"];

/**
 * One availability for a logical project made of several physical checkouts.
 *
 * A logical project is one repository seen from several machines, and those
 * checkouts disagree all the time — a stale worktree nobody cleaned up sits
 * "missing" next to a healthy clone. Reporting the row as missing on that
 * basis dims a project that is perfectly fine to work in, so a single
 * "available" member is enough to call the whole row available.
 *
 * "Missing" is reserved for the case where EVERY member is missing, because
 * that is the only state in which there is genuinely nothing to open.
 *
 * `undefined` is not a third state to reason about — it is a legacy snapshot
 * that never carried the field. Members that report nothing are dropped rather
 * than counted, and a project where none of them report leaves the field
 * undefined so the UI treats it as live. Counting them as "missing" would mark
 * every pre-probe project broken.
 */
export function aggregateProjectAvailability(
  members: readonly { readonly availability?: ProjectAvailability }[],
): ProjectAvailability {
  const reported = members
    .map((member) => member.availability)
    .filter((value): value is NonNullable<ProjectAvailability> => value !== undefined);

  if (reported.length === 0) return undefined;
  if (reported.some((value) => value === "available")) return "available";
  if (reported.every((value) => value === "missing")) return "missing";
  return "unknown";
}
